import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Send } from "lucide-react";
import { servicePages } from "../data/servicePages";
import { testimonials } from "../mockData";
import { Toaster, toast } from "sonner";
import { usePopup } from "../context/PopupContext";
import { submitLead } from "../utils/api";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = servicePages[slug];

  if (!service) {
    return (
      <main className="pt-28 pb-16 px-4 text-center">
        <h1 className="text-4xl text-white font-['Playfair_Display']">Service Not Found</h1>
        <Link to="/" className="text-purple-400 mt-4 inline-block">Go Home</Link>
      </main>
    );
  }

  return (
    <main data-testid="service-detail-page" className="pt-28 pb-0">
      <Toaster position="top-right" />
      <HeroSection service={service} />
      <AboutSection service={service} />
      <PlatformsSection service={service} />
      <TechStackSection service={service} />
      <PortfolioSection />
      <ProcessSection service={service} />
      <WhyPartnerSection service={service} />
      <TestimonialsSection />
      <IndustriesSection service={service} />
      <ContactFormSection />
    </main>
  );
}

/* ─── Hero with gradient aurora bg ─── */
function HeroSection({ service }) {
  const { open } = usePopup();
  return (
    <section className="relative py-28 px-4 overflow-hidden" data-testid="service-hero">
      {/* Aurora background */}
      <div className="absolute inset-0 bg-[#08080f]">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-purple-600/20 rounded-full blur-[120px]" />
        <div className="absolute top-20 left-1/4 w-[300px] h-[300px] bg-indigo-600/10 rounded-full blur-[100px]" />
        <div className="absolute top-10 right-1/4 w-[250px] h-[250px] bg-purple-500/10 rounded-full blur-[80px]" />
      </div>
      <div className="relative max-w-4xl mx-auto text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Playfair_Display'] text-white mb-6 leading-tight">
            {service.title}
          </h1>
          <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            {service.subtitle}
          </p>
          <button
            onClick={open}
            className="mt-10 inline-flex items-center gap-2 cta-button text-white px-10 py-4 rounded-full text-base font-semibold"
            data-testid="service-hero-cta"
          >
            Request a Quote
          </button>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── About with image ─── */
function AboutSection({ service }) {
  const { open } = usePopup();
  return (
    <section className="py-24 px-4 bg-[#f8f8fc]">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="lg:order-2">
          <div className="rounded-3xl overflow-hidden shadow-2xl">
            <img src={service.image} alt={service.title} className="w-full h-[250px] sm:h-[420px] object-cover" loading="lazy" />
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="lg:order-1">
          <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-gray-900 mb-6">
            <span className="text-purple-600">Custom solutions,</span> backed by innovation & expertise
          </h2>
          <p className="text-gray-600 text-base leading-relaxed mb-4">{service.heroDescription}</p>
          <p className="text-gray-600 text-base leading-relaxed">{service.detailDescription}</p>
          <div className="flex justify-center lg:justify-start">
            <button
              onClick={open}
              className="mt-8 inline-flex items-center gap-2 cta-button text-white px-8 py-4 rounded-full text-sm font-semibold"
            >
              Request Proposal <ArrowRight size={16} />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Platforms with bg image cards ─── */
function PlatformsSection({ service }) {
  const [active, setActive] = useState(0);
  const platform = service.platforms[active];

  const platformImages = [
    "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1200&h=600&fit=crop",
    "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=1200&h=600&fit=crop",
    "https://images.unsplash.com/photo-1504639725590-34d0984388bd?w=1200&h=600&fit=crop",
    "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1200&h=600&fit=crop",
  ];

  return (
    <section className="py-24 px-4 bg-[#08080f]">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-white text-center mb-12">
          Types of development <span className="gradient-text">platforms</span> we work with
        </h2>

        {/* Tabs - scrollable on mobile */}
        <div className="flex gap-3 mb-10 overflow-x-auto pb-2 sm:flex-wrap sm:justify-center sm:overflow-visible scrollbar-hide">
          {service.platforms.map((p, i) => (
            <button
              key={p.name}
              onClick={() => setActive(i)}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
                i === active
                  ? "bg-purple-600 text-white"
                  : "bg-white/5 text-gray-400 hover:text-white border border-purple-900/20"
              }`}
              data-testid={`platform-tab-${i}`}
            >
              {p.name}
            </button>
          ))}
        </div>

        {/* Content card with background image */}
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="relative rounded-3xl overflow-hidden min-h-[400px] flex items-center"
        >
          <div className="absolute inset-0">
            <img src={platformImages[active]} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a1a]/90 via-[#0a0a1a]/70 to-[#0a0a1a]/40" />
          </div>
          <div className="relative z-10 p-8 sm:p-12 max-w-2xl">
            <h3 className="text-xl sm:text-2xl font-semibold text-white mb-4">{platform.title}</h3>
            <p className="text-gray-300 text-base leading-relaxed mb-8">{platform.description}</p>
            <div className="flex gap-8">
              {service.platforms[active] && (
                <>
                  <div className="border-l-2 border-purple-500 pl-4">
                    <span className="text-3xl font-bold text-white">98%</span>
                    <p className="text-gray-400 text-sm mt-1">Client Satisfaction</p>
                  </div>
                  <div className="border-l-2 border-purple-500 pl-4">
                    <span className="text-3xl font-bold text-white">50+</span>
                    <p className="text-gray-400 text-sm mt-1">Projects Delivered</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Tech Stack ─── */
function TechStackSection({ service }) {
  const { techStack } = service;
  const categories = [
    { label: "Frameworks & Tools", items: techStack.frameworks },
    { label: "Cloud & Infrastructure", items: techStack.cloud },
    { label: "Languages & Technologies", items: techStack.languages },
  ];

  return (
    <section className="py-24 px-4 bg-[#f8f8fc]">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-gray-900 text-center mb-4">
          Technology <span className="text-purple-600">Stack</span>
        </h2>
        <p className="text-gray-600 text-center max-w-2xl mx-auto mb-14">
          We leverage the latest and most reliable technologies to deliver robust, scalable solutions.
        </p>

        <div className="lg:grid lg:grid-cols-3 lg:gap-8 mobile-slider">
          {categories.map((cat) => (
            <div key={cat.label} className="w-[280px] lg:w-auto bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
              <h3 className="text-gray-900 font-semibold text-lg mb-5">{cat.label}</h3>
              <div className="flex flex-wrap gap-3">
                {cat.items.map((item) => (
                  <span key={item} className="bg-purple-50 text-purple-700 text-sm px-4 py-2 rounded-full border border-purple-200 font-medium">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Portfolio / Ideas into Reality ─── */
function PortfolioSection() {
  const projects = [
    { title: "Dashboard UI", label: "Web Development", image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=500&h=400&fit=crop" },
    { title: "Mobile App", label: "App Development", image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=500&h=400&fit=crop" },
    { title: "E-Commerce Platform", label: "Web Development", image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=500&h=400&fit=crop" },
    { title: "Analytics Tool", label: "AI Development", image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=500&h=400&fit=crop" },
    { title: "Brand Identity", label: "Creative Design", image: "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=500&h=400&fit=crop" },
  ];

  return (
    <section className="py-24 px-4 bg-[#08080f] overflow-hidden">
      <div className="max-w-7xl mx-auto mb-12">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-white text-center mb-4">
          Ideas into <span className="gradient-text">reality</span>
        </h2>
        <p className="text-gray-400 text-center max-w-2xl mx-auto">
          Component-focused development is key to a performant solution. Explore our recent work across industries.
        </p>
      </div>
      <div className="overflow-hidden">
        <div className="flex animate-marquee-slow">
          {[...projects, ...projects, ...projects].map((p, i) => (
            <div key={i} className="flex-shrink-0 w-[calc(100vw-2rem)] lg:w-[400px] mx-4 rounded-2xl overflow-hidden group cursor-pointer relative">
              <img src={p.image} alt={p.title} className="w-full h-[300px] object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-5 left-5">
                <h4 className="text-white font-bold text-base">{p.title}</h4>
                <p className="text-gray-300 text-xs">{p.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Process ─── */
function ProcessSection({ service }) {
  const icons = ["💡", "🎨", "⚙️", "✅", "🚀", "🔧"];
  return (
    <section className="py-24 px-4 bg-[#f8f8fc]">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-gray-900 text-center mb-4">
          Our simple, frictionless <span className="text-purple-600">development workflow</span>
        </h2>
        <p className="text-gray-600 text-center max-w-2xl mx-auto mb-14">
          We use lean, agile methodology for collaborative development and faster turnarounds.
        </p>

        <div className="lg:grid lg:grid-cols-3 lg:gap-6 mobile-slider">
          {service.process.map((step, i) => (
            <div key={step.step} className="w-[260px] lg:w-auto bg-white rounded-2xl p-8 border border-gray-200 shadow-sm card-hover relative">
              <div className="flex items-start justify-between mb-4">
                <span className="text-purple-600 text-3xl font-bold font-['Playfair_Display']">{step.step}</span>
                <span className="text-2xl">{icons[i]}</span>
              </div>
              <h3 className="text-gray-900 font-semibold text-lg mb-2">{step.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Why Partner ─── */
function WhyPartnerSection({ service }) {
  const whyImages = [
    "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=500&h=350&fit=crop",
    "https://images.unsplash.com/photo-1551434678-e076c223a692?w=500&h=350&fit=crop",
    "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=500&h=350&fit=crop",
  ];

  return (
    <section className="py-24 px-4 bg-[#08080f]">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-white text-center mb-14">
          Why partner with <span className="gradient-text">Digitronix Consultancy</span>
        </h2>

        <div className="lg:grid lg:grid-cols-3 lg:gap-8 mobile-slider">
          {service.whyPartner.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="w-[280px] lg:w-auto bg-white/5 border border-purple-900/20 rounded-2xl overflow-hidden card-hover"
            >
              <img src={whyImages[i]} alt={item.title} className="w-full h-48 object-cover" loading="lazy" />
              <div className="p-6">
                <h3 className="text-white font-semibold text-lg mb-3">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Testimonials ─── */
function TestimonialsSection() {
  return (
    <section className="py-24 px-4 bg-[#0a0a14] overflow-hidden">
      <div className="max-w-7xl mx-auto mb-14">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-white text-center mb-4">
          Over <span className="text-purple-400">100,000+</span> people trust us
        </h2>
        <p className="text-gray-400 text-center max-w-2xl mx-auto">
          From concept to delivery, we are committed to working with our clients to create solutions that are intuitive, engaging, and user-friendly.
        </p>
      </div>
      <div className="overflow-hidden">
        <div className="flex animate-marquee">
          {[...testimonials, ...testimonials, ...testimonials].map((t, i) => (
            <div key={i} className="flex-shrink-0 w-[calc(100vw-2rem)] lg:w-[380px] mx-4 bg-gradient-to-br from-purple-900/20 to-transparent border border-purple-800/30 rounded-2xl p-6">
              <p className="text-gray-300 text-sm leading-relaxed mb-6 italic">"{t.quote}"</p>
              <div className="flex items-center gap-3">
                <img src={t.image} alt={t.name} className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                <div>
                  <h4 className="text-white font-semibold text-sm">{t.name}</h4>
                  <p className="text-gray-500 text-xs">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Industries ─── */
function IndustriesSection({ service }) {
  const [activeIndustry, setActiveIndustry] = useState(0);
  const { open } = usePopup();

  const industryImages = [
    "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=400&fit=crop",
    "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop",
    "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&h=400&fit=crop",
    "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=600&h=400&fit=crop",
  ];

  return (
    <section className="py-24 px-4 bg-[#f8f8fc]">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-gray-900 text-center mb-4">
          Custom <span className="text-purple-600">solutions</span> for different <span className="text-purple-600">industries</span>
        </h2>
        <p className="text-gray-600 text-center max-w-2xl mx-auto mb-12">
          Our custom solutions cater to the unique demands of diverse industries, delivering high-performance applications that drive growth.
        </p>

        {/* Industry Tabs - scrollable on mobile */}
        <div className="flex gap-3 mb-10 overflow-x-auto pb-2 sm:flex-wrap sm:justify-center sm:overflow-visible scrollbar-hide">
          {service.industries.map((ind, i) => (
            <button
              key={ind}
              onClick={() => setActiveIndustry(i)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                i === activeIndustry
                  ? "bg-purple-600 text-white"
                  : "bg-white text-gray-600 hover:text-purple-600 border border-gray-200"
              }`}
              data-testid={`industry-tab-${i}`}
            >
              {ind}
            </button>
          ))}
        </div>

        <motion.div
          key={activeIndustry}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="grid lg:grid-cols-2 gap-12 items-center"
        >
          <div className="rounded-2xl overflow-hidden shadow-lg lg:order-2">
            <img src={industryImages[activeIndustry]} alt={service.industries[activeIndustry]} className="w-full h-[220px] sm:h-[300px] object-cover" loading="lazy" />
          </div>
          <div className="lg:order-1">
            <p className="text-gray-700 text-base leading-relaxed mb-6">{service.industryDescriptions[activeIndustry]}</p>
            <div className="flex justify-center lg:justify-start">
              <button
                onClick={open}
                className="inline-flex items-center gap-2 cta-button text-white px-6 py-3 rounded-full text-sm font-semibold"
              >
                Let's Discuss Your Project <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Contact Form ─── */
function ContactFormSection() {
  const [formData, setFormData] = useState({ name: "", email: "", countryCode: "+974", phone: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => /^\d{10}$/.test(phone);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData({ ...formData, phone: value });
    if (errors.phone) setErrors({ ...errors, phone: "" });
  };

  const countryCodes = [
    { code: "+1", country: "US" }, { code: "+44", country: "UK" }, { code: "+974", country: "QA" },
    { code: "+971", country: "AE" }, { code: "+966", country: "SA" }, { code: "+91", country: "IN" },
    { code: "+92", country: "PK" }, { code: "+61", country: "AU" }, { code: "+49", country: "DE" },
    { code: "+33", country: "FR" }, { code: "+86", country: "CN" }, { code: "+81", country: "JP" },
    { code: "+82", country: "KR" }, { code: "+55", country: "BR" }, { code: "+234", country: "NG" },
    { code: "+27", country: "ZA" }, { code: "+65", country: "SG" }, { code: "+60", country: "MY" },
    { code: "+62", country: "ID" }, { code: "+20", country: "EG" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!validateEmail(formData.email)) newErrors.email = "Please enter a valid email (e.g., user@example.com)";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    else if (!validatePhone(formData.phone)) newErrors.phone = "Please enter exactly 10 digits after country code";
    if (!formData.message.trim()) newErrors.message = "Description is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setSubmitted(true);
    try {
      await submitLead({ name: formData.name, email: formData.email, phone: formData.phone, countryCode: formData.countryCode, message: formData.message, source: "service-page" });
      toast.success("Project inquiry submitted!");
    } catch (err) {
      toast.success("Project inquiry submitted!");
    }
    setFormData({ name: "", email: "", countryCode: "+974", phone: "", message: "" });
    setTimeout(() => setSubmitted(false), 3000);
  };

  return (
    <section className="py-24 px-4 bg-[#08080f]">
      <div className="max-w-2xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-white text-center mb-4">
          Start your <span className="gradient-text">project</span>
        </h2>
        <p className="text-gray-400 text-center mb-10">
          If you need a fresh, innovative approach, let's talk. We'll craft a custom solution that elevates your brand.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="service-contact-form">
          <div>
            <input
              type="text"
              placeholder="Your Name"
              value={formData.name}
              onChange={(e) => { setFormData({ ...formData, name: e.target.value }); if (errors.name) setErrors({ ...errors, name: "" }); }}
              className={`w-full bg-white/5 border ${errors.name ? "border-red-500" : "border-purple-900/30"} rounded-xl px-5 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500`}
              data-testid="service-form-name"
            />
            {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
          </div>
          <div>
            <input
              type="text"
              placeholder="Email Address"
              value={formData.email}
              onChange={(e) => { setFormData({ ...formData, email: e.target.value }); if (errors.email) setErrors({ ...errors, email: "" }); }}
              className={`w-full bg-white/5 border ${errors.email ? "border-red-500" : "border-purple-900/30"} rounded-xl px-5 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500`}
              data-testid="service-form-email"
            />
            {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
          </div>
          <div>
            <div className="flex gap-2">
              <select
                value={formData.countryCode}
                onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                className="bg-white/5 border border-purple-900/30 rounded-xl px-2 py-4 text-white text-sm focus:outline-none focus:border-purple-500 w-[90px] appearance-none"
                data-testid="service-form-country"
              >
                {countryCodes.map((c) => (
                  <option key={c.code} value={c.code} className="bg-[#0d0d1a]">{c.code} {c.country}</option>
                ))}
              </select>
              <input
                type="tel"
                placeholder="Phone (10 digits)"
                value={formData.phone}
                onChange={handlePhoneChange}
                className={`flex-1 min-w-0 bg-white/5 border ${errors.phone ? "border-red-500" : "border-purple-900/30"} rounded-xl px-4 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500`}
                data-testid="service-form-phone"
              />
            </div>
            {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
          </div>
          <div>
            <textarea
              placeholder="Describe your project..."
              rows={4}
              value={formData.message}
              onChange={(e) => { setFormData({ ...formData, message: e.target.value }); if (errors.message) setErrors({ ...errors, message: "" }); }}
              className={`w-full bg-white/5 border ${errors.message ? "border-red-500" : "border-purple-900/30"} rounded-xl px-5 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none`}
              data-testid="service-form-message"
            />
            {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}
          </div>
          <button
            type="submit"
            className="w-full cta-button text-white px-8 py-4 rounded-full text-base font-semibold flex items-center justify-center gap-2"
            data-testid="service-form-submit"
          >
            {submitted ? "Submitted!" : <><Send size={18} /> Submit</>}
          </button>
        </form>
      </div>
    </section>
  );
}
